import React, { useEffect, useState } from "react"
import { db } from "../../utils/firebase"
import { NavLink } from "react-router-dom"
import moment from "moment"
import { ExternalLink } from "lucide-react"

/**
 * UI components
 */
import Badge from "../ui/badge/badge"

/**
 * Functional component to return the booking
 */
function Booking(props) {
	const [client, setClient] = useState({})
	const [agent, setAgent] = useState({})
	const [returnDate, setReturnDate] = useState("")
	const [supplierInvoices, setSupplierInvoices] = useState([])
	const [bookingItems, setBookingItems] = useState([])

	/**
	 * Deconstruct the details from the props
	 */
	const {
		id,
		details,
		update_local_totals,
		update_local_pax,
		client_prefetch,
		agent_prefetch,
		documents_prefetch,
		supplier_invoices_prefetch,
		booking_hotels_prefetch,
		booking_rounds_prefetch,
		hotels_map,
		courses_map,
		transfers_map,
		show_site,
		show_agent,
		show_lead_pax,
		show_email,
		show_returning,
		show_no_pax,
		show_location,
		show_itemised_invoices,
	} = props

	/**
	 * Get the margin values from the sales margins
	 */
	const { flights_cost, agents_margin, customer_price, profit } = details?.margins || {}

	const { golfers, non_golfers } = details

	/**
	 * Generate a string to represent the check_in date from the timestamps
	 */
	const checkInDate = moment(details.check_in?.seconds, "X").format("MMM D[,] YYYY")
	const bookedDate = moment(details.booked?.seconds, "X").format("MMM D[,] YYYY")

	/**
	 * Work out a margin for the booking
	 */
	const bookingProfitPercent = profit >= 0 && customer_price > 0 ? ((profit / customer_price) * 100).toFixed(2) : -100

	/**
	 * Find the total gropu size for the booking
	 */
	const groupSize = details.golfers + details.non_golfers

	/**
	 *
	 */
	useEffect(() => {
		update_local_totals(id, "MARGIN", details?.margins?.profit)
		update_local_totals(id, "COMMISION", agents_margin)

		const flying = !isNaN(parseInt(golfers)) ? parseInt(golfers) : 0
		const notFlying = !isNaN(parseInt(non_golfers)) ? parseInt(non_golfers) : 0

		update_local_pax(id, flying + notFlying, flights_cost > 0)
	}, [])

	/**
	 * On component load - fetch client and agent data conditionally
	 */
	useEffect(() => {
		// Only fetch client if columns require it AND no prefetch provided
		if ((show_lead_pax || show_email) && !client_prefetch && details.client) {
			db.doc(`clients/${details.client}`)
				.get()
				.then((clientDoc) => setClient(clientDoc.data()))
		} else if (client_prefetch) {
			setClient(client_prefetch)
		}

		// Only fetch agent if column requires it AND no prefetch provided
		if (show_agent && !agent_prefetch && details.agent) {
			db.doc(`users/${details.agent}`)
				.get()
				.then((agentDoc) => setAgent(agentDoc.data()))
		} else if (agent_prefetch) {
			setAgent(agent_prefetch)
		}

		// Supplier invoices (always needed)
		if (supplier_invoices_prefetch && supplier_invoices_prefetch.length >= 0) {
			setSupplierInvoices(supplier_invoices_prefetch)
		} else {
			// Fallback to individual fetch if not prefetched
			db.collection(`bookings/${id}/supplier_invoices`)
				.orderBy("created", "desc")
				.get()
				.then((invoiceDocs) => {
					const invoices = []
					invoiceDocs.forEach((doc) => {
						invoices.push({ id: doc.id, ...doc.data() })
					})
					setSupplierInvoices(invoices)
				})
		}
	}, [show_lead_pax, show_email, show_agent, client_prefetch, agent_prefetch, supplier_invoices_prefetch])

	/**
	 * Calculate return date from booking hotels if showing returning column
	 */
	useEffect(() => {
		if (show_returning && booking_hotels_prefetch && booking_hotels_prefetch.length > 0) {
			let firstCheckIn = null
			let totalNights = 0
			booking_hotels_prefetch.forEach((hotel) => {
				const { check_in, nights } = hotel
				totalNights += nights
				if (!firstCheckIn || check_in.seconds < firstCheckIn) {
					firstCheckIn = check_in.seconds
				}
			})
			setReturnDate(moment(firstCheckIn, "X").add(totalNights, "days").format("MMM D[,] YYYY"))
		}
	}, [show_returning, booking_hotels_prefetch])

	/**
	 * Process booking items using prefetched data
	 */
	useEffect(() => {
		if (!booking_hotels_prefetch || !booking_rounds_prefetch || !hotels_map || !courses_map || !transfers_map) {
			return
		}

		// Process hotels with prefetched hotel data
		const hotelData = booking_hotels_prefetch.map((hotel) => {
			const hotelDoc = hotels_map[hotel.resortID] || {}
			return {
				...hotel,
				hotelID: hotel.id,
				finance_type: "hotel",
				finance_id: hotel.id,
				finance_currency: hotelDoc.currency,
				finance_email: hotelDoc.email,
				finance_name: hotelDoc.name,
			}
		})

		// Process rounds with prefetched course data
		const roundsData = booking_rounds_prefetch.map((round) => {
			const courseDoc = courses_map[round.courseID] || {}
			return {
				...round,
				roundID: round.id,
				finance_type: "round",
				finance_id: round.id,
				finance_currency: courseDoc.currency,
				finance_email: courseDoc.email,
				finance_name: courseDoc.name,
			}
		})

		// Process transfer with prefetched transfer data
		const transferData = []
		if (details.transfer) {
			const transferDoc = transfers_map[details.transfer] || {}
			transferData.push({
				transferID: details.transfer,
				finance_type: "transfer",
				finance_id: details.transfer,
				finance_currency: transferDoc.currency,
				finance_email: transferDoc.email,
				finance_name: transferDoc.name,
			})
		}

		setBookingItems([...hotelData, ...roundsData, ...transferData])
	}, [booking_hotels_prefetch, booking_rounds_prefetch, hotels_map, courses_map, transfers_map, details.transfer])

	return (
		<tr>
			<td>
				{details.missing_values && <div className="no-values-tag"></div>}

				{bookedDate}
			</td>
			<td>{details?.reference}</td>
			{show_site && <td>{details?.enquiry?.site}</td>}
			{show_agent && (
				<td>
					{agent?.first_name} {agent?.last_name}
				</td>
			)}
			{show_lead_pax && (
				<td>
					{client.first_name && `${client.first_name} ${client.last_name}`}
					{!client.first_name && <small className="no-value">No name set</small>}
				</td>
			)}
			{show_email && <td>{client.email}</td>}
			<td>{checkInDate}</td>
			{show_returning && (
				<td>
					{returnDate}
					{!returnDate && <small className="no-value">Return not calculated</small>}
				</td>
			)}
			{show_no_pax && <td>{groupSize}</td>}
			{show_location && <td>{details.booked_location ? details.booked_location : details.enquiry?.location}</td>}
			<td>£{details?.margins?.flights_cost?.toLocaleString() || 0}</td>
			<td>£{details?.margins?.customer_price?.toLocaleString() || 0}</td>
			<td>£{details?.paid_by_client?.toLocaleString() || 0}</td>
			<td>£{details?.margins?.supplier_invoices?.toLocaleString() || 0}</td>
			{show_itemised_invoices && (
				<td className="supplier-invoices-cell">
					{bookingItems.length > 0 ? (
						bookingItems.map((invoice, index) => (
							<div
								key={invoice.id}
								className="invoice-line">
								<small style={{ color: "#000" }}>
									{`${invoice.finance_type?.toUpperCase()} | `}
									{`${invoice.finance_name} | `}
									{invoice.finance_type === "round" && <>{`${invoice.cost_per_golfer?.toLocaleString() || 0}`} </>}
									{invoice.finance_type === "hotel" && <>{`${invoice.total_cost?.toLocaleString() || 0}`} </>}
									{invoice.finance_type === "transfer" && <>{`${invoice.total_cost?.toLocaleString() || 0}`} </>}
									{invoice.finance_currency?.toUpperCase()}
								</small>
							</div>
						))
					) : (
						<small className="no-value">No invoices attached</small>
					)}
				</td>
			)}
			<td className="supplier-invoices-cell">
				{supplierInvoices.length > 0 ? (
					supplierInvoices.map((invoice, index) => (
						<div
							key={invoice.id}
							className="invoice-line">
							<small style={{ color: "#000" }}>
								{`${invoice.amount?.toLocaleString() || "-"} ${invoice.currency || "-"} | `}
								{`Ref: ${invoice.external_reference || "-"} | `}
								{`Status: ${invoice.payment_status || "-"}`}
							</small>
						</div>
					))
				) : (
					<small className="no-value">No invoices attached</small>
				)}
			</td>
			<td className="supplier-documents-cell">
				{documents_prefetch && documents_prefetch.length > 0 ? (
					documents_prefetch.map((document, index) => (
						<div
							key={document.id}
							className="document-line">
							<a
								href={document.url}
								target="_blank"
								rel="noopener noreferrer"
								style={{
									display: "flex",
									alignItems: "center",
									gap: "4px",
									color: "#000",
									textDecoration: "underline",
								}}>
								{document.title || document.file_name}
								<ExternalLink size={14} />
							</a>
						</div>
					))
				) : (
					<small className="no-value">No documents</small>
				)}
			</td>
			<td>
				£{details?.margins?.profit?.toLocaleString()} ({bookingProfitPercent}%)
			</td>
			<td>£{agents_margin?.toLocaleString()}</td>
			<td>
				{details.confirmed && (
					<Badge
						label="Yes"
						type="POSITIVE"
					/>
				)}

				{!details.confirmed && (
					<Badge
						label="No"
						type="NEGATIVE"
					/>
				)}
			</td>
			<td>
				{details.resorts_paid && (
					<Badge
						label="Yes"
						type="POSITIVE"
					/>
				)}

				{!details.resorts_paid && (
					<Badge
						label="No"
						type="NEGATIVE"
					/>
				)}
			</td>

			<td className="is-button">
				<NavLink to={`booking/${id}`}>View booking</NavLink>
				<a
					target="_blank"
					rel="noreferrer"
					href={`booking/${id}`}>
					View in new tab
				</a>
			</td>
		</tr>
	)
}

export default Booking
